import { isDiscordMode } from '../../utils/discord';
import { DEPLOYMENT_ENV, isProdEnv } from '../../utils/environment';
import isServer from '../../utils/isServer';
import vars from '../../vars';
import sentryErrorTypes from '../sentryErrorTypes';

const deploymentEnv = isDiscordMode()
  ? `${DEPLOYMENT_ENV}-discord`
  : DEPLOYMENT_ENV;
// To avoid potential conflicts with source map resolution, we are appending the deployment env to the release name
// for environments that don't bump their release version numbers, which could be confused for the production release.
const releaseVersion = ['development', 'experimental'].includes(DEPLOYMENT_ENV)
  ? `${RELEASE_VERSION}-${deploymentEnv}`
  : RELEASE_VERSION;

const ignoreErrorsSet = new Set(['ApiError', 'OpmlError']); // TODO: move to ignoreErrors after web-api-client change

export const ignoreThirdPartyUrls = [
  /cdn\.doubleverify\.com/,
  /c\.amazon-adsystem\.com/,
  /imasdk\.googleapis\.com/,
  /cdn\.cookielaw\.org/,
  /cdn\.flashtalking\.com/,
  /s\.yimg\.com/,
  /pdp-cdn\.retargetly\.com/,
  /securepubads\.g\.doubleclick\.net/,
];

// https://docs.sentry.io/platforms/javascript/configuration/
export const sentryConfig = {
  release: releaseVersion,
  environment: deploymentEnv,
  // Set to true for debugging during development. Also, uncomment __SENTRY_DEBUG__ env vars in webpack configs if
  // debugging prod builds.
  debug: false,
  enableTracing: true,
  sampleRate: isProdEnv() ? 0.5 : 1.0,
  // Set tracesSampleRate to 1.0 to capture 100%
  // of transactions for performance monitoring.
  // We recommend adjusting this value in production
  tracesSampleRate: isProdEnv() ? 0.001 : 1.0,
  maxBreadcrumbs: isProdEnv() ? 25 : 100,
  denyUrls: isProdEnv() ? ignoreThirdPartyUrls : [],
  beforeSend: (event, hint) => {
    // https://docs.sentry.io/platforms/javascript/configuration/filtering/#filtering-events
    // Ignore explicit error types that we don't want to send to Sentry as they are currently sent to NewRelic
    if (ignoreErrorsSet.has[hint?.originalException?.name]) {
      return null; // have to return null to ignore an event
    }

    if (event?.exception?.values?.[0]?.type === sentryErrorTypes.URIError) {
      return null; // have to return null to ignore an event
    }

    // Ignore Electron errors from the desktop app until we upgrade Electron
    if (
      !isServer() &&
      window.navigator?.userAgent?.includes('Electron/4.2.8')
    ) {
      return null; // have to return null to ignore an event
    }

    // Temporarily ignore errors triggered from hls.js
    const { stacktrace } = event.exception?.values?.[0] || {};

    if (
      stacktrace?.frames?.some?.((frame) =>
        frame?.module?.includes(
          'assets/js/gemini-web/node_modules/hls.js/dist/hls',
        ),
      )
    ) {
      // Return null to drop the event completely
      return null;
    }

    return event;
  },
};

export const sentryServerDsn = vars.get('SENTRY_DSN_SERVER');
export const sentryClientDsn = vars.get('SENTRY_DSN_CLIENT');
